import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiSession } from '../../types/aai';
import { Action, Chip, Drawer, ErrorNote, Field, Kicker, Loading, Select } from '../../components/aa';
import { whenLabel } from '../partner/format';
import { SESSION_TYPE_LABEL } from './trainer';

/*
 * One session, opened to run it.
 *
 * A session row used to hand the trainer off to the whole calendar, which is
 * the one thing a row that names a session should not do. Everything running a
 * session needs is here: who is invited, who turned up, and marking it held.
 * Attendance is per person because the trainer took the register; it is never
 * rolled into anyone's readiness.
 */

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-card border border-line bg-canvas-inset px-5 py-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1.5 break-words text-[15px] font-bold text-ink">{value}</div>
  </div>
);

const shortName = (email: string): string => email.split('@')[0];

/*
 * What the session left behind, in the vocabulary the record accepts
 * (aai_session.deliverable_type: skill | doc | slides | meeting). `meeting`
 * means the session itself was the thing — nothing to keep. The old delivery
 * page sent the session TYPE here, which this endpoint refuses, so its "Mark
 * complete" button answered 400 and the session stayed planned.
 */
const LEFT_BEHIND = [
  { value: 'meeting', label: 'Nothing to keep, we talked it through' },
  { value: 'doc', label: 'A document or written walkthrough' },
  { value: 'slides', label: 'Slides' },
  { value: 'skill', label: 'A skill they can run again' },
];

export const SessionDrawer: React.FC<{
  session: AaiSession;
  onChanged: () => void;
  onClose: () => void;
}> = ({ session, onChanged, onClose }) => {
  const [members, setMembers] = useState<string[] | null>(null);
  const [present, setPresent] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(session.status === 'completed');
  const [leftBehind, setLeftBehind] = useState(session.deliverable_type || 'meeting');

  const load = useCallback(async () => {
    if (!session.cohort_id) {
      setMembers([]);
      return;
    }
    try {
      const [cohort, attendance] = await Promise.all([
        aaiApi.getCohort(session.cohort_id),
        aaiApi.getAttendance(session.id),
      ]);
      const map: Record<string, boolean> = {};
      attendance.data.attendance.forEach((a) => { map[a.member_email] = a.attended; });
      setMembers(cohort.data.members || []);
      setPresent(map);
    } catch {
      setError('Could not load who is invited to this session.');
      setMembers([]);
    }
  }, [session.cohort_id, session.id]);

  useEffect(() => { void load(); }, [load]);

  const saveAttendance = async () => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.recordAttendance(session.id, present);
      onChanged();
      toast.success('Register saved.');
    } catch {
      setError('Could not save the register. Nothing was recorded, so you can try again.');
    } finally {
      setBusy(false);
    }
  };

  const markHeld = async () => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.setDeliverable(session.id, { deliverable_type: leftBehind });
      setCompleted(true);
      onChanged();
      toast.success('Recorded as held.');
    } catch {
      setError('Could not mark this session held.');
    } finally {
      setBusy(false);
    }
  };

  const attended = Object.values(present).filter(Boolean).length;
  const invited = (members || []).length;

  let badge = <Chip tone="warn" dot>Planned</Chip>;
  if (completed) badge = <Chip tone="ok" dot>Held</Chip>;

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>
      {invited > 0 && !completed && (
        <Action size="lg" variant="quiet" onClick={saveAttendance} disabled={busy} reason="Saving">Save the register</Action>
      )}
      {!completed && <Action size="lg" onClick={markHeld} disabled={busy} reason="Recording">Mark held</Action>}
    </div>
  );

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Delivery</Kicker>}
      badge={badge}
      title={session.title}
      sub={completed
        ? 'This session is recorded as held. The register stays on the record.'
        : 'Take the register while you run it, then mark it held. Attendance is a delivery record and never feeds anyone’s readiness.'}
      footer={footer}
    >
      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      <div className="grid gap-3 sm:grid-cols-2">
        <Tile label="When" value={whenLabel(session.scheduled_at) || 'No date set'} />
        <Tile label="Kind" value={SESSION_TYPE_LABEL[session.session_type] || session.session_type} />
        <Tile label="Cohort" value={session.cohort_name || 'No cohort, a 1:1 or open session'} />
        <Tile label="Length" value={session.duration_min ? `${session.duration_min} minutes` : 'Not recorded'} />
        {session.module_title && <Tile label="Covers" value={session.module_title} />}
        {session.module_week && <Tile label="Program week" value={session.module_week} />}
      </div>

      {!completed && (
        <div className="mt-6">
          <Field label="What did the session leave behind?" htmlFor="session-left" hint="Recorded with the session so a later reader knows what came out of it.">
            <Select id="session-left" value={leftBehind} onChange={(e) => setLeftBehind(e.target.value)}>
              {LEFT_BEHIND.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
        </div>
      )}

      {completed && session.deliverable_type && (
        <p className="m-0 mt-5 text-[13px] leading-[21px] text-ink-dim">
          Left behind: {LEFT_BEHIND.find((o) => o.value === session.deliverable_type)?.label || session.deliverable_type}.
        </p>
      )}

      <div className="mt-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Register</div>
          {invited > 0 && <span className="text-[12px] text-ink-soft">{attended} of {invited} marked present</span>}
        </div>
        {members === null && <Loading label="Loading who is invited" />}
        {members !== null && invited === 0 && (
          <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
            This session has no cohort, so there is no register to take. Mark it held once you have run it.
          </p>
        )}
        {invited > 0 && (
          <div className="divide-y divide-line-soft rounded-card border border-line">
            {(members || []).map((email) => (
              <label key={email} className="flex cursor-pointer items-center gap-3 px-4 py-3">
                <input
                  type="checkbox"
                  checked={Boolean(present[email])}
                  disabled={completed}
                  onChange={(e) => setPresent((prev) => ({ ...prev, [email]: e.target.checked }))}
                  className="h-4 w-4 shrink-0 rounded border-line-strong text-ink focus:ring-ink/20"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold text-ink">{shortName(email)}</span>
                  <span className="block text-[12px] text-ink-soft">{email}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default SessionDrawer;
